import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import Icon from '../components/docs/Icon'
import { america, findMission, missions } from '../content/missions'
import { usePrefs } from '@/state/prefs'
import Cased from '@/components/ui/Cased'

function Download({ href, children }: { href: string; children: string }) {
  return (
    <a className="mds-button mds-button--secondary mds-button--sm" href={href} download>
      <Icon name="download" size={16} />
      {children}
    </a>
  )
}

export default function DesignToolsPage() {
  const { prefs } = usePrefs()
  const pickerId = useId()
  const [id, setId] = useState(() => (findMission(prefs.mission) ?? america).id)
  const mission = findMission(id) ?? america

  return (
    <DocLayout
      eyebrow="Start"
      title="Design tools"
      intro="MDS for designers: a Figma plugin and variables, Adobe swatches, Sketch, Penpot and Canva files for every mission theme, generated from the same tokens as the code."
    >
      <section className="doc-section">
        <h2>
          <Cased>Choose a mission</Cased>
        </h2>
        <p>The downloads below are for this mission’s theme, in light and dark.</p>
        <div className="mds-field dtl-picker">
          <label className="mds-label" htmlFor={pickerId}>
            Mission
          </label>
          <select id={pickerId} className="mds-select" value={id} onChange={(event) => setId(event.target.value)}>
            {missions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      </section>
      <section className="doc-section" aria-labelledby="figma-title">
        <h2 id="figma-title">Figma</h2>
        <p>
          The MDS plugin builds the whole library into a Figma file: color variables in light and dark, size variables,
          text and effect styles, every component, and the page templates, with every color bound to its variable. Its
          mission picker restyles all of it to any agency.
        </p>
        <div className="mds-cluster">
          <Download href="/mds/figma/mds-figma-plugin.zip">Figma plugin</Download>
        </div>
        <ol className="dtl-steps">
          <li>Unzip the plugin. In the Figma desktop app, open a design file.</li>
          <li>Choose Plugins, then Development, then Import plugin from manifest, and pick its manifest.json.</li>
          <li>Run Mission Design System, choose a mission and select Build library.</li>
          <li>To publish it for your team, publish the file as a library.</li>
        </ol>
        <h3 className="mds-h3">Variables only</h3>
        <p>Figma also reads design token files as variables directly, one file for each mode.</p>
        <div className="mds-cluster">
          <Download href={`/mds/tokens/${mission.id}/light.tokens.json`}>Light tokens</Download>
          <Download href={`/mds/tokens/${mission.id}/dark.tokens.json`}>Dark tokens</Download>
        </div>
        <ol className="dtl-steps">
          <li>Open the Variables panel and create a collection.</li>
          <li>Drag both files onto the collection. Each becomes a mode: Light and Dark.</li>
          <li>Colors, spacing, radii, type sizes, durations and font families arrive as variables, grouped as in the code.</li>
        </ol>
      </section>
      <section className="doc-section" aria-labelledby="adobe-title">
        <h2 id="adobe-title">Adobe</h2>
        <p>
          A swatch library for Illustrator, Photoshop and InDesign, with a light and a dark group of every color role. Added
          to a Creative Cloud Library, it also reaches Adobe Express.
        </p>
        <div className="mds-cluster">
          <Download href={`/mds/swatches/${mission.id}.ase`}>{`${mission.short} swatches (.ase)`}</Download>
          <Download href="/mds/design/pdf/spec-sheet.pdf">Spec sheet (PDF)</Download>
        </div>
        <p>
          Every component and template is also an editable SVG and every template a PDF, from its own page. Illustrator
          opens them as layers with live text.
        </p>
        <ol className="dtl-steps">
          <li>In the Swatches panel menu, choose Open Swatch Library, then Other Library, and pick the file.</li>
          <li>To share it, select the color group and add it to a Creative Cloud Library.</li>
        </ol>
      </section>
      <section className="doc-section" aria-labelledby="penpot-title">
        <h2 id="penpot-title">Penpot</h2>
        <p>Penpot’s open-source design tool reads token sets and themes. The file has a core set and a set for each mode.</p>
        <div className="mds-cluster">
          <Download href={`/mds/tokens/${mission.id}/penpot.tokens.json`}>Penpot tokens</Download>
        </div>
        <ol className="dtl-steps">
          <li>In the Tokens panel, open Tools and choose Import.</li>
          <li>Switch between the Light and Dark themes to restyle the file.</li>
        </ol>
      </section>
      <section className="doc-section" aria-labelledby="sketch-title">
        <h2 id="sketch-title">Sketch</h2>
        <p>A Sketch library with every color role as a Color Variable, in light and dark, and the type scale as text styles.</p>
        <div className="mds-cluster">
          <Download href={`/mds/sketch/${mission.id}.sketch`}>Sketch library</Download>
        </div>
        <ol className="dtl-steps">
          <li>In Sketch settings, under Libraries, choose Add Library and pick the file.</li>
          <li>Its colors and text styles then appear in every document.</li>
        </ol>
      </section>
      <section className="doc-section" aria-labelledby="canva-title">
        <h2 id="canva-title">Canva</h2>
        <p>
          A brand pack for a Canva Brand Kit: the palette as hex values and as a sheet, the agency’s seal or logo, and the
          steps. Canva’s font library includes the MDS typefaces.
        </p>
        <div className="mds-cluster">
          <Download href={`/mds/canva/${mission.id}.zip`}>{`${mission.short} brand pack`}</Download>
        </div>
        <ol className="dtl-steps">
          <li>In Canva, open Brand, then your Brand Kit.</li>
          <li>Add the colors from colors.txt and the mark from the pack, and set the typefaces it names.</li>
          <li>Component and template SVGs, and the spec sheet PDF, upload as designs to build from.</li>
        </ol>
      </section>
      <section className="doc-section" aria-labelledby="spec-title">
        <h2 id="spec-title">
          <Cased>Spec sheet</Cased>
        </h2>
        <p>
          One printable page of the current design: color roles in light and dark, typefaces and the type scale, space,
          shape and every component. Print it or save it as a PDF from the browser.
        </p>
        <p>
          <Link className="mds-button mds-button--secondary mds-button--sm" to="/design-tools/spec">
            Open the spec sheet
          </Link>
        </p>
      </section>
      <section className="doc-section">
        <h2>
          <Cased>Your own choices</Cased>
        </h2>
        <p>
          The Edit Design download on the home page carries these files for the colors, typefaces and shape you chose, beside
          the stylesheets and fonts.
        </p>
      </section>
    </DocLayout>
  )
}
